import { RecentlyViewed } from '@theme/recently-viewed-products';
import { buildProductCard } from './salon-product-card.js';

// Total cards to load; visible columns remain controlled by the section.
const MAX_DISPLAY = 24;
const HISTORY_KEY = 'salon_recently_viewed_handles_v2';
const MAX_HISTORY = MAX_DISPLAY + 1;
let memoryHistory = [];

function getHistory(currentHandle) {
  let saved = memoryHistory;
  try {
    const value = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    if (Array.isArray(value)) saved = value;
  } catch (error) { /* Keep this page usable when storage is unavailable. */ }
  let legacy = [];
  try {
    const value = RecentlyViewed.getProducts();
    if (Array.isArray(value)) legacy = value;
  } catch (error) { /* Older history is optional. */ }
  // Our own key preserves 24 previous products plus the current product.
  const ordered = [currentHandle, ...saved, ...legacy].filter(isHandle);
  const history = [...new Set(ordered)].slice(0, MAX_HISTORY);
  memoryHistory = history;
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch (error) {}
  return history;
}

function isHandle(value) {
  return typeof value === 'string' && value.length > 0 && !/^\d+$/.test(value);
}

async function init(root) {
  const grid = root.querySelector('[data-recently-viewed-grid]');
  const cardSectionId = root.dataset.cardSectionId;
  const currentHandle = root.dataset.currentProductHandle;
  const history = getHistory(currentHandle);
  // Keep compatibility with other theme features, without relying on their limit.
  try { if (currentHandle) RecentlyViewed.addProduct(currentHandle); } catch (error) {}
  if (!grid || !cardSectionId) { root.hidden = true; return; }

  const templateWrapper = document.querySelector(`[id$="__${CSS.escape(cardSectionId)}"]`);
  const template = templateWrapper?.querySelector('.salon-product-card');
  const handles = history
    .filter(handle => isHandle(handle) && handle !== currentHandle)
    .slice(0, MAX_DISPLAY);
  if (!template || !handles.length) { root.hidden = true; return; }

  // Fetch in small batches, retaining the original newest-first order.
  const cards = [];
  for (let offset = 0; offset < handles.length; offset += 4) {
    const results = await Promise.allSettled(
      handles.slice(offset, offset + 4).map(handle => buildProductCard(template, handle))
    );
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) cards.push(result.value);
      else console.warn('A recently viewed product card could not load.', result.status === 'rejected' ? result.reason : 'No card returned');
    }
  }
  if (!root.isConnected) return;
  if (!cards.length) { root.hidden = true; return; }
  const fragment = document.createDocumentFragment();
  for (const card of cards) {
    const item = document.createElement('div');
    item.className = 'resource-list__item';
    item.appendChild(card);
    fragment.appendChild(item);
  }
  grid.replaceChildren(fragment);
  root.hidden = false;
}

function initAll(scope = document) {
  const roots = [...scope.querySelectorAll('[data-recently-viewed-products]')];
  if (scope.matches?.('[data-recently-viewed-products]')) roots.unshift(scope);
  for (const root of roots) {
    init(root).catch(error => console.error('Recently viewed products could not load:', error));
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
} else {
  initAll();
}
document.addEventListener('shopify:section:load', event => initAll(event.target));
